// EQUIPO EN LA VITRINA (Carlos 29-sep-2026): quién es STAFF y con qué descuento compran staff y maestros.
// Ajuste `vitrina_equipo` = { staff_user_ids, discount_pct }. Lo usa la Venta de vitrina (pestañas
// Maestro / Staff, descuento aplicado y visible al cobrar o fiar) y POST /payments (amount_discount).
//  · Staff = los marcados aquí (hoy Karla, Dayan, Mabel, Aracely).
//  · Maestro = quien da clase o tiene el puesto, sin marcar como staff.
//  · Ni uno ni otro (Carlos, Brenda…) = compra como público general y no sale en la Venta.
import { useEffect, useState } from 'react';
import { Save, Percent } from 'lucide-react';
import { getSetting, putSetting } from '../../../services/settings';
import { getStaffBuyers, StaffBuyer, money } from '../../finances/api';
import { useToast } from '../../../context/ToastContext';

const inputCls = 'rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

export default function VitrinaEquipoCard() {
  const toast = useToast();
  const [pct, setPct] = useState('');
  const [staffIds, setStaffIds] = useState<number[]>([]);
  const [orig, setOrig] = useState('');
  const [gente, setGente] = useState<StaffBuyer[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [cargado, setCargado] = useState(false);

  useEffect(() => {
    Promise.all([getSetting('vitrina_equipo').catch(() => null), getStaffBuyers(true).catch(() => null)]).then(([s, g]) => {
      if (s) {
        try {
          const o = JSON.parse(s.value || '{}');
          const ids: number[] = (o.staff_user_ids || []).map(Number);
          setPct(String(o.discount_pct ?? 0)); setStaffIds(ids);
          setOrig(JSON.stringify({ p: Number(o.discount_pct ?? 0), ids: [...ids].sort() }));
        } catch { /* se queda vacío */ }
      }
      if (g) setGente(g.data);
      setCargado(true);
    });
  }, []);
  if (!cargado) return null;

  const dirty = JSON.stringify({ p: Number(pct) || 0, ids: [...staffIds].sort() }) !== orig;
  const esStaff = (id: number) => staffIds.includes(id);
  const toggle = (id: number) => setStaffIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // Cómo queda cada persona con lo marcado ahora (sin guardar), para verlo antes de dar Guardar.
  const tipoDe = (u: StaffBuyer) => (esStaff(u.user_id) ? 'staff' : u.es_profe ? 'maestro' : 'publico');
  const ejemplo = 100 - (Number(pct) || 0);

  async function guardar() {
    const p = Number(pct);
    if (!Number.isFinite(p) || p < 0 || p >= 100) { toast.error('El descuento debe ir de 0 a 99 %'); return; }
    setSaving(true);
    try {
      await putSetting('vitrina_equipo', JSON.stringify({ staff_user_ids: staffIds, discount_pct: p }));
      setOrig(JSON.stringify({ p, ids: [...staffIds].sort() }));
      toast.success('Equipo de vitrina guardado');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-sm" data-testid="mi-estudio-equipo">
      <h2 className="flex items-center gap-2 font-display text-base font-bold text-content"><Percent size={17} className="text-brand-blue" /> Equipo en la vitrina</h2>
      <p className="mb-4 mt-0.5 text-xs text-content-3">
        Staff y maestros compran con descuento; se aplica solo y se ve al cobrar o al fiar. Quien no es ni staff ni maestro compra como público.
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Descuento para el equipo</label>
        <div className="flex items-center gap-1.5">
          <input type="number" min="0" max="99" step="1" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="equipo-pct" className={`${inputCls} w-24`} />
          <span className="text-sm text-content-3">% · un artículo de $100 les sale en {money(ejemplo)}</span>
        </div>
      </div>
      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Quién es staff</label>
        <p className="mb-2 text-xs text-content-3">Marca a recepción y administración. Los profesores sin marcar quedan como maestros; los demás, como público.</p>
        {gente == null ? (
          <p className="text-xs text-content-3">No se pudo cargar al equipo.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2" data-testid="equipo-lista">
            {gente.map((u) => {
              const t = tipoDe(u);
              return (
                <li key={u.user_id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-content hover:bg-surface-2">
                    <input type="checkbox" checked={esStaff(u.user_id)} onChange={() => toggle(u.user_id)} className="h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
                    <span className="min-w-0 flex-1 truncate">{u.name}</span>
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                      t === 'staff' ? 'bg-accent/20 text-accent-ink' : t === 'maestro' ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'bg-surface-2 text-content-3'}`}>
                      {t === 'publico' ? 'público' : t}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button onClick={guardar} disabled={saving || !dirty}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent-solid px-3.5 py-2 text-sm font-semibold text-on-accent transition disabled:bg-line disabled:text-content-3">
        <Save size={15} /> {saving ? 'Guardando…' : dirty ? 'Guardar cambios' : 'Sin cambios'}
      </button>
    </section>
  );
}
